import type { TRootState } from "@/reducers";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { datadogLogs } from "@datadog/browser-logs";
import type {
  ICart,
  ICartAttributesUpdateMutation,
  ICartBuyerIdentityUpdateMutation,
  ICartCreateInputMutation,
  ICartCreateMutation,
  ICartDiscountCodesUpdateMutation,
  ICartLineItem,
  ICartLinesAddMutation,
  ICartLinesRemoveMutation,
  ICartLinesUpdateMutation,
} from "@repo/shopify/src/queries/cart-gql";
import {
  cartAttributesUpdate,
  cartBuyerIdentityUpdateMutation,
  cartCreateInputMutation,
  cartCreateMutation,
  cartDiscountCodesUpdateMutation,
  cartLineItemQuery,
  cartLinesAddMutation,
  cartLinesRemoveMutation,
  cartLinesUpdateMutation,
  cartQuery,
} from "@repo/shopify/src/queries/cart-gql";
import type { Cart } from "@shopify/hydrogen-react/storefront-api-types";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { cartCreator } from "@reducers/cart";
import { layoutActionCreator } from "@reducers/layout";

const useCart = () => {
  const dispatch = useDispatch();

  const updateCart = useCallback((cart: Cart) => {
    dispatch(cartCreator.updateCart(cart));
  }, []);

  const [createCart] = useMutation<ICartCreateMutation>(gql(cartCreateMutation.query), {
    onCompleted: (data: ICartCreateMutation) => {
      if (data.cartCreate.cart) dispatch(cartCreator.updateCart(data.cartCreate.cart));
    },
    onError: (error) => {
      datadogLogs.logger.error("[SHOPIFY][cartCreate] Failed to mutation", error);
      throw new Error(`[SHOPIFY][cartCreate] Failed to mutation : ${JSON.stringify(error)}`);
    },
  });

  const [createCartWithParams] = useMutation<ICartCreateInputMutation>(gql(cartCreateInputMutation.query), {
    onCompleted: (data: ICartCreateMutation) => {
      if (data.cartCreate.cart) dispatch(cartCreator.updateCart(data.cartCreate.cart));
    },
    onError: (error) => {
      datadogLogs.logger.error("[SHOPIFY][CartCreateInput] Failed to mutation", error);
      throw new Error(`[SHOPIFY][CartCreateInput] Failed to mutation : ${JSON.stringify(error)}`);
    },
  });

  /** 카트 아이템 할인코드 제어 */
  const [removeCartDiscountCode] = useMutation<ICartDiscountCodesUpdateMutation>(
    gql(cartDiscountCodesUpdateMutation.query),
    {
      onCompleted: (data) => {
        if (data.cartDiscountCodesUpdate.cart) dispatch(cartCreator.updateCart(data.cartDiscountCodesUpdate.cart));
      },
      onError: (error) => {
        datadogLogs.logger.error("[SHOPIFY][CartDiscountCodesUpdate] Failed to mutation", error);
        throw new Error(`[SHOPIFY][CartDiscountCodesUpdate] Failed to mutation : ${JSON.stringify(error)}`);
      },
    },
  );

  /** 카트 아이템 할인코드 작용 */
  const [discountMutate] = useMutation<ICartDiscountCodesUpdateMutation>(gql(cartDiscountCodesUpdateMutation.query), {
    onCompleted: (data) => {
      if (data.cartDiscountCodesUpdate.cart) dispatch(cartCreator.updateCart(data.cartDiscountCodesUpdate.cart));
    },
    onError: (error) => {
      datadogLogs.logger.error("[SHOPIFY][CartDiscountCodesUpdate] Failed to mutation", error);
      throw new Error(`[SHOPIFY][CartDiscountCodesUpdate] Failed to mutation : ${JSON.stringify(error)}`);
    },
  });

  /** 카트 아이템 수량 업데이트 */
  const [updateCartLineBase, { loading: updateCartLoading }] = useMutation<ICartLinesUpdateMutation>(
    gql(cartLinesUpdateMutation.query),
    {
      onCompleted: (data) => {
        if (data.cartLinesUpdate.cart) dispatch(cartCreator.updateCart(data.cartLinesUpdate.cart));
      },
      onError: (error) => {
        datadogLogs.logger.error("[SHOPIFY][CartLinesUpdate] Failed to mutation", error);
        throw new Error(`[SHOPIFY][CartLinesUpdate] Failed to mutation : ${JSON.stringify(error)}`);
      },
    },
  );

  /** 카트 아이템 제거 */
  const [removeCartLineBase] = useMutation<ICartLinesRemoveMutation>(gql(cartLinesRemoveMutation.query), {
    onCompleted: (data) => {
      if (data.cartLinesRemove.cart) dispatch(cartCreator.updateCart(data.cartLinesRemove.cart));
    },
    onError: (error) => {
      datadogLogs.logger.error("[SHOPIFY][CartLinesRemove] Failed to mutation", error);
      throw new Error(`[SHOPIFY][CartLinesRemove] Failed to mutation : ${JSON.stringify(error)}`);
    },
  });

  /** 카트 아이템 추가 */
  const [addCartLine, { loading: addCartLoading }] = useMutation<ICartLinesAddMutation>(
    gql(cartLinesAddMutation.query),
    {
      onCompleted: (data) => {
        if (data.cartLinesAdd.cart) dispatch(cartCreator.updateCart(data.cartLinesAdd.cart));
        dispatch(layoutActionCreator.openCart());
      },
      onError: (error) => {
        datadogLogs.logger.error("[SHOPIFY][CartLinesAdd] Failed to mutation", error);
        throw new Error(`[SHOPIFY][CartLinesAdd] Failed to mutation : ${JSON.stringify(error)}`);
      },
    },
  );

  /** 카트 조회 */
  const [getCart, { loading: getCartLoading }] = useLazyQuery<ICart>(gql(cartQuery.query), {
    onCompleted: (data) => {
      if (data.cart) dispatch(cartCreator.updateCart(data.cart));
    },
    onError: (error) => {
      datadogLogs.logger.error("[SHOPIFY][Cart] Failed to query", error);
      throw new Error(`[SHOPIFY][Cart] Failed to query : ${JSON.stringify(error)}`);
    },
  });

  const [setBuyerIdentity] = useMutation<ICartBuyerIdentityUpdateMutation>(gql(cartBuyerIdentityUpdateMutation.query), {
    onCompleted: (data) => {
      if (data) dispatch(cartCreator.updateCart(data.cartBuyerIdentityUpdate.cart));
    },
    onError: (error) => {
      datadogLogs.logger.error("[SHOPIFY][CartBuyerIdentityUpdate] Failed to mutation", error);
      throw new Error(JSON.stringify(error));
    },
  });

  const [getCartLineItem, { data: cartLineItem }] = useLazyQuery<ICartLineItem>(gql(cartLineItemQuery.query), {
    fetchPolicy: "no-cache",
    onError: (error) => {
      datadogLogs.logger.error("[SHOPIFY][Cart] Failed to query", error);
      throw new Error(`[SHOPIFY][Cart] Failed to query : ${JSON.stringify(error)}`);
    },
  });

  const [setCartAttributes] = useMutation<ICartAttributesUpdateMutation>(gql(cartAttributesUpdate.query), {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (data.cartAttributesUpdate.cart) dispatch(cartCreator.updateCart(data.cartAttributesUpdate.cart));
    },
    onError: (error) => {
      datadogLogs.logger.error("[SHOPIFY][CartLineItem] Failed to setCartAttribute", error);
      throw new Error(`[SHOPIFY][CartLineItem] Failed to setCartAttribute : ${JSON.stringify(error)}`);
    },
  });

  return {
    cart: useSelector((state: TRootState) => state.cart),
    updateCart,
    createCart,
    removeCartDiscountCode,
    discountMutate,
    createCartWithParams,
    updateCartLineBase,
    updateCartLoading,
    removeCartLineBase,
    addCartLine,
    addCartLoading,
    getCart,
    getCartLoading,
    getCartLineItem,
    setBuyerIdentity,
    cartLineItem,
    setCartAttributes,
  };
};

export default useCart;
